import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { isFavorited, toggleFavorite, displayFavCount } from '../lib/favorites';
import { useFavorites } from '../lib/useFavorites';
import { openInDoubao } from '../lib/doubao';
import { shareArticle } from '../lib/share';
import { highlightText } from '../lib/highlight';
import { googleZhUrl } from '../lib/links';

// LLM 生成的英文 tag → topics.json 的中文主题名映射
const TAG_TO_TOPIC = {
  agent: 'Agent 智能体',
  rl: '强化学习',
  rag: 'RAG 检索增强',
  reasoning: '推理与思维链',
  memory: '记忆系统',
  benchmark: '评估与基准',
  evaluation: '评估与基准',
  architecture: '模型架构与训练',
  training: '模型架构与训练',
  efficiency: '模型架构与训练',
  coding: '工程实践与 Coding Agent',
  research: 'AI 科研',
  multimodal: '其他',
  alignment: '其他',
};

// 统一论文卡片：精选 / 主题 / 收藏 / 周报 共用。
// 结构（参考设计图）：
//   顶部：来源标签（主题）
//   标题（点击进解读页）
//   中文摘要（3 行截断，hover title 看完整）
//   tags（#agent #llm...） + 发布/翻译时间
//   推荐理由（底部高亮块）
//   底部操作：收藏(计数) | view pdf | view arxiv | open doubao
export default function PaperCard({ paper, highlight = '', navIds = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  useFavorites(); // 订阅收藏变化以重渲染计数/高亮
  const fav = isFavorited(paper.id);

  // 解读页链接：ids（来源列表顺序，供上/下一篇）+ from（来源页路径，供侧边栏高亮）。
  // 同时写入 query：中键/右键新标签页打开时浏览器走原生跳转，react-router 的 state 不传递，
  // 需由 ArticlePage 从 query 兜底读取。
  const articleHref = `/article/${paper.id}?from=${encodeURIComponent(location.pathname)}${navIds && navIds.length ? `&ids=${navIds.join(',')}` : ''}`;

  const tags = paper.tags && paper.tags.length ? paper.tags : (paper.topic ? [paper.topic] : []);

  return (
    <article className="pcard">
      {paper.topic && (
        <div
          className="pcard-source clickable"
          title={`查看主题：${paper.topic}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/topics?topic=${encodeURIComponent(paper.topic)}`); }}
        >
          {paper.topic}
        </div>
      )}

      <Link to={articleHref} className="pcard-title" state={{ ids: navIds || [], from: location.pathname }}>{highlightText(paper.title, highlight)}</Link>

      {(paper.summary_zh || paper.excerpt_zh) && (
        <p className="pcard-summary">{highlightText(paper.summary_zh || paper.excerpt_zh, highlight)}</p>
      )}

      <div className="pcard-meta">
        <div className="pcard-tags">
          {tags.map((t, i) => (
            <span
              key={i}
              className="pcard-tag clickable"
              title={`查看主题：${TAG_TO_TOPIC[t] || t}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/topics?topic=${encodeURIComponent(TAG_TO_TOPIC[t] || t)}`); }}
            >
              #{t}
            </span>
          ))}
        </div>
        {paper.pub_date && <time className="pcard-date">{paper.pub_date}</time>}
      </div>

      {paper.reason_zh && (
        <div className="pcard-reason">
          <span className="pcard-reason-label">推荐理由</span>
          <span>{paper.reason_zh}</span>
        </div>
      )}

      <div className="pcard-actions">
        <button
          className={`pcard-btn${fav ? ' active' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleFavorite(paper); }}
          title={fav ? '取消收藏' : '收藏'}
        >
          <i className={`${fav ? 'fas' : 'far'} fa-star`} />
          <span>{displayFavCount(paper, fav)}</span>
        </button>
        <Link className="pcard-btn" to={articleHref} state={{ ids: navIds || [], from: location.pathname }} title="查看中文解读">
          <i className="fas fa-book-open" /><span>解读</span>
        </Link>
        <button
          className="pcard-btn"
          title="分享（复制公开链接）"
          onClick={(e) => { e.stopPropagation(); shareArticle(paper.id); }}
        >
          <i className="fas fa-share-alt" /><span>分享</span>
        </button>
        {paper.pdf_url && (
          <a className="pcard-icon" href={paper.pdf_url} target="_blank" rel="noreferrer" title="View PDF" onClick={(e) => e.stopPropagation()}>
            <i className="fas fa-file-pdf" />
          </a>
        )}
        {paper.arxiv_url && (
          <a className="pcard-icon" href={paper.arxiv_url} target="_blank" rel="noreferrer" title="View on arXiv" onClick={(e) => e.stopPropagation()}>
            <i className="fas fa-external-link-alt" />
          </a>
        )}
        {paper.arxiv_id && (
          <a
            className="pcard-icon"
            href={googleZhUrl(paper.arxiv_id)}
            target="_blank"
            rel="noreferrer"
            title="谷歌全文翻译（翻译 arXiv HTML 版整页）"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fab fa-google" />
          </a>
        )}
        {paper.pdf_url && (
          <button
            className="pcard-icon"
            title="豆包翻译（复制提示词并打开豆包，粘贴发送）"
            onClick={(e) => { e.stopPropagation(); openInDoubao(paper.pdf_url); }}
          >
            <img className="pcard-doubao-icon" src="/doubao-icon.png" alt="豆包" />
          </button>
        )}
      </div>
    </article>
  );
}
